import { Avatar, AvatarFallback, AvatarImage } from '@ophelos/ui/components/avatar';
import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { SidebarMenuButton } from '@ophelos/ui/components/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { cn } from '@ophelos/ui/lib/utils';
import { Check, ChevronRight, ChevronsUpDown, Folders, Layers, Loader2, Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub } from '@/contexts/HubContext';
import { isNoDebtTypeCohort, makeNoDebtTypeCohort } from '@/lib/debt-type-cohort';
import { useCell } from '@/lib/global/cell-context';
import type { DirectoryCell } from '@/lib/global/cell-store';
import { useDemoContextLabel } from '@/lib/presentation-mode/store';
import { isGlobalSurface } from '@/lib/surface-mode';
import { getInitials } from '@/lib/user-helpers';
import { fetchContextDebtTypesForClients } from '@/services/context';
import type { Operator } from '@/types';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { type ScopePresentation, useShellPresentation } from './ShellPresentationContext';

export type SwitcherOrgRow = {
  id: string;
  name: string;
  subtitle: string;
  avatarUrl?: string | null;
};

function operatorToOrgRow(op: Operator): SwitcherOrgRow {
  const bits = [op.headquarters_country, op.default_currency].filter(Boolean);
  return { id: op.id, name: op.name, subtitle: bits.join(' · '), avatarUrl: op.avatar_url };
}

function cellToOrgRow(cell: DirectoryCell): SwitcherOrgRow {
  return {
    id: cell.operator_id,
    name: cell.name,
    subtitle: `${cell.headquarters_country} · ${cell.code}`,
    avatarUrl: null,
  };
}

export function SidebarContextSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  if (isGlobalSurface()) return <GlobalSidebarContextSwitcher collapsed={collapsed} />;
  return <LocalSidebarContextSwitcher collapsed={collapsed} />;
}

function LocalSidebarContextSwitcher({ collapsed }: { collapsed: boolean }) {
  const { operators, selectedOperator, setSelectedOperator } = useHub();
  const [switching, setSwitching] = React.useState(false);

  const onOrgSwitch = async (id: string) => {
    if (!selectedOperator || id === selectedOperator.id) return;
    const operator = operators.find((op) => op.id === id);
    if (!operator?.workos_organization_id) return;
    setSwitching(true);
    try {
      await setSelectedOperator(operator);
    } catch (error) {
      console.error('Failed to switch operator:', error);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <SidebarSwitcherShell
      collapsed={collapsed}
      orgs={operators.map(operatorToOrgRow)}
      selectedOrgId={selectedOperator?.id ?? ''}
      onOrgSwitch={onOrgSwitch}
      orgColumnLabel="Operator"
      switching={switching}
    />
  );
}

function GlobalSidebarContextSwitcher({ collapsed }: { collapsed: boolean }) {
  const { cells, cell, selectCell } = useCell();
  return (
    <SidebarSwitcherShell
      collapsed={collapsed}
      orgs={cells.map(cellToOrgRow)}
      selectedOrgId={cell.operator_id}
      onOrgSwitch={(id) => {
        const next = cells.find((c) => c.operator_id === id);
        if (next) selectCell(next);
      }}
      orgColumnLabel="Market"
    />
  );
}

function SidebarSwitcherShell({
  collapsed = false,
  orgs,
  selectedOrgId,
  onOrgSwitch,
  orgColumnLabel,
  switching: switchingFromParent,
}: {
  collapsed?: boolean;
  orgs: SwitcherOrgRow[];
  selectedOrgId: string;
  onOrgSwitch: (id: string) => void | Promise<void>;
  orgColumnLabel: string;
  switching?: boolean;
}) {
  const { selectedOperator, clients, selectedClient, setSelectedClient, setOperatorWideView, isOperatorWideView, contextSwitcherRequest } =
    useHub();
  const { scope: presentedScope } = useShellPresentation();
  const [allDebtTypes, setAllDebtTypes] = React.useState<ReturnType<typeof useHub>['debtTypes']>([]);
  // An empty `allDebtTypes` is ambiguous on its own — in flight, failed, and genuinely
  // empty all look alike — and a client row only skips the debt-type column when it can
  // prove the client has none. Nothing is proven until a fetch succeeds.
  const [debtTypesLoaded, setDebtTypesLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const refreshDebtTypes = React.useCallback(async () => {
    if (!selectedOperator || clients.length === 0) {
      setAllDebtTypes([]);
      setDebtTypesLoaded(true);
      return;
    }
    try {
      const data = await fetchContextDebtTypesForClients(clients.map((c) => c.id));
      setAllDebtTypes(data);
      setDebtTypesLoaded(true);
    } catch (error) {
      console.error('Failed to fetch debt types:', error);
      setDebtTypesLoaded(false);
    }
  }, [selectedOperator, clients]);

  React.useEffect(() => {
    void refreshDebtTypes();
  }, [refreshDebtTypes]);

  const { selectedDebtType, setSelectedDebtType, debtTypes } = useHub();
  // Demo mode only relabels: the first demo client fronts the switcher while the real scope is untouched.
  const demoLabel = useDemoContextLabel();
  const [switching, setSwitching] = React.useState(false);
  const busy = Boolean(switchingFromParent) || switching;
  const { hasPermission } = useUserPermissions();
  const navigate = useNavigate();
  const canCreateClient = hasPermission('config:admin');
  const canCreateDebtType = hasPermission('debt_types:write');
  const legacyLabel = demoLabel ?? selectedClient?.display_name ?? 'All Clients';
  // Preserve an app's explicit null/false/undefined presentation; only an absent presentation uses legacy Hub scope.
  const scope: ScopePresentation = presentedScope ?? {
    title: selectedDebtType ? selectedDebtType.name : selectedClient || demoLabel ? legacyLabel : (selectedOperator?.name ?? ''),
    subtitle: selectedDebtType ? legacyLabel : selectedClient || demoLabel ? (selectedOperator?.name ?? '') : 'All Clients',
    label: legacyLabel,
    imageUrl: demoLabel ? null : (selectedClient?.logo_url ?? selectedOperator?.avatar_url ?? null),
    operatorAvatar: isOperatorWideView && !demoLabel,
    selectedClient,
    operatorSelected: isOperatorWideView,
    mineSelected: false,
    operatorOption: { label: 'All Clients', onSelect: setOperatorWideView },
    showDebtTypes: true,
    onClientSelect: setSelectedClient,
    onConfirmScope: (client, debtType) => {
      setSelectedClient(client);
      setSelectedDebtType(debtType);
    },
  };
  const showDebtTypes = scope.showDebtTypes;
  const activeScopeClient = scope.selectedClient;
  const contextLabel = scope.label;
  const isOperatorScopeSelected = scope.operatorSelected;
  const operatorOption = scope.operatorOption;
  const triggerTitle = scope.title;
  const triggerSubtitle = scope.subtitle;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) void refreshDebtTypes();
  };

  // A page asked for the switcher (see `requestContextSwitcher`). Depends on the
  // request value alone — `refreshDebtTypes` changes identity whenever `clients`
  // does, and the open effect must not re-run for that. Seeding the ref with the
  // current value means mounting never pops the panel open.
  const handledRequest = React.useRef(contextSwitcherRequest);
  React.useEffect(() => {
    if (handledRequest.current === contextSwitcherRequest) return;
    handledRequest.current = contextSwitcherRequest;
    setOpen(true);
  }, [contextSwitcherRequest]);

  if (!selectedOperator) return null;

  const handleOrgSwitch = (id: string) => {
    if (id === selectedOrgId) return;
    void onOrgSwitch(id);
  };

  /** Select a client and keep the popover open so the Debt Types column can appear. */
  const handleClientSelect = (client: (typeof clients)[0]) => {
    scope.onClientSelect(client);
  };

  /** Apply client + debt-type scope and close the popover. */
  const handleConfirmDebtTypeScope = async (client: (typeof clients)[0], debtType: (typeof debtTypes)[0] | null) => {
    setSwitching(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      scope.onConfirmScope(client, debtType);
      setOpen(false);
    } finally {
      setSwitching(false);
    }
  };

  const handleOperatorWideView = async () => {
    if (!operatorOption || isOperatorScopeSelected) return;
    setSwitching(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      operatorOption.onSelect();
      setOpen(false);
    } finally {
      setSwitching(false);
    }
  };

  const handleCreateDebtType = (client: (typeof clients)[0]) => {
    setSelectedClient(client);
    navigate('/settings/debt-types');
    setOpen(false);
  };

  const contextImageUrl = scope.imageUrl;
  const useOperatorAvatarStyle = scope.operatorAvatar;

  const panels = (
    <SwitcherPanels
      orgs={orgs}
      selectedOrgId={selectedOrgId}
      orgColumnLabel={orgColumnLabel}
      clients={clients}
      selectedClient={activeScopeClient ?? null}
      isOperatorWideView={isOperatorScopeSelected}
      showOperatorBook={Boolean(operatorOption)}
      operatorBookLabel={operatorOption?.label ?? 'All Clients'}
      showMine={Boolean(scope.mineOption)}
      mineSelected={scope.mineSelected}
      onMine={() => {
        scope.mineOption?.onSelect();
        setOpen(false);
      }}
      switching={busy}
      canCreateClient={canCreateClient}
      canCreateDebtType={canCreateDebtType}
      debtTypes={showDebtTypes ? debtTypes : []}
      selectedDebtType={showDebtTypes ? selectedDebtType : null}
      allDebtTypes={showDebtTypes ? allDebtTypes : []}
      debtTypesLoaded={!showDebtTypes || debtTypesLoaded}
      onOrgSwitch={handleOrgSwitch}
      onClientSelect={handleClientSelect}
      onOperatorWideView={handleOperatorWideView}
      onConfirmDebtTypeScope={handleConfirmDebtTypeScope}
      onCreateClient={() => {
        navigate('/settings/onboarding');
        setOpen(false);
      }}
      onCreateDebtType={handleCreateDebtType}
    />
  );

  if (collapsed) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex">
              <PopoverTrigger asChild>
                <Button
                  size="icon-sm"
                  disabled={busy}
                  className={cn(
                    'group cursor-pointer flex items-center rounded-lg border text-left text-sm transition-colors',
                    open ? 'bg-card border-border hover:bg-soft!' : 'bg-card border-border hover:bg-soft hover:border-border-heavy'
                  )}
                >
                  {busy ? (
                    <Loader2 className="size-4.5 animate-spin text-muted-foreground" />
                  ) : (
                    <Avatar className="size-6.5 rounded-md">
                      {contextImageUrl && <AvatarImage src={contextImageUrl} alt={contextLabel} />}
                      <AvatarFallback
                        className={cn(
                          'text-[10px] font-semibold rounded-sm',
                          useOperatorAvatarStyle
                            ? 'bg-[#FFE1D6]/40 text-[#CC332B] dark:bg-[#CC332B]/15 dark:text-[#FF7557]'
                            : 'highlight-accent'
                        )}
                      >
                        {useOperatorAvatarStyle ? getInitials(selectedOperator.name) : getInitials(contextLabel)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </Button>
              </PopoverTrigger>
            </div>
          </TooltipTrigger>
          {!open && (
            <TooltipContent side="right" align="center">
              {selectedDebtType ? `${triggerTitle} · ${triggerSubtitle}` : contextLabel}
            </TooltipContent>
          )}
        </Tooltip>
        <PopoverContent side="right" align="start" sideOffset={8} className="w-auto! p-0 rounded-xl">
          {panels}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          size="xl"
          disabled={busy}
          className={cn(
            'group w-full cursor-pointer flex items-center gap-2 rounded-lg p-2 pl-1 border text-left text-sm transition-colors',
            open ? 'bg-card border-border hover:bg-soft!' : 'bg-card border-border hover:bg-soft hover:border-border-heavy'
          )}
        >
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <div className="flex aspect-square size-8 items-center justify-center rounded-md">
              {busy ? (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              ) : (
                <Avatar className="size-8 rounded-sm">
                  {contextImageUrl && <AvatarImage src={contextImageUrl} alt={contextLabel} />}
                  <AvatarFallback
                    className={cn(
                      'text-xs font-semibold rounded-md',
                      useOperatorAvatarStyle
                        ? 'bg-[#FFE1D6]/40 text-[#CC332B] dark:bg-[#CC332B]/15 dark:text-[#FF7557]'
                        : 'bg-accent text-accent-foreground'
                    )}
                  >
                    {useOperatorAvatarStyle ? getInitials(selectedOperator.name) : getInitials(contextLabel)}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>

            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{triggerTitle}</span>
              <span className="truncate text-xs text-muted-foreground">{triggerSubtitle}</span>
            </div>
          </div>
          <ChevronsUpDown className="size-3 text-muted-foreground shrink-0" />
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={8} alignOffset={-20} className="w-auto! p-0 rounded-xl">
        {panels}
      </PopoverContent>
    </Popover>
  );
}

/** Exported for unit tests (empty-client panel + write-gated create). */
export function SwitcherPanels({
  orgs,
  selectedOrgId,
  orgColumnLabel,
  clients,
  selectedClient,
  isOperatorWideView,
  switching,
  canCreateClient,
  canCreateDebtType,
  debtTypes,
  selectedDebtType,
  allDebtTypes,
  debtTypesLoaded,
  onOrgSwitch,
  onClientSelect,
  onOperatorWideView,
  onCreateClient,
  onConfirmDebtTypeScope,
  onCreateDebtType,
  showOperatorBook = true,
  operatorBookLabel = 'All Clients',
  showMine = false,
  mineSelected = false,
  onMine,
}: {
  orgs: SwitcherOrgRow[];
  selectedOrgId: string;
  orgColumnLabel: string;
  clients: ReturnType<typeof useHub>['clients'];
  selectedClient: ReturnType<typeof useHub>['selectedClient'];
  isOperatorWideView: boolean;
  switching: boolean;
  canCreateClient: boolean;
  canCreateDebtType: boolean;
  debtTypes: ReturnType<typeof useHub>['debtTypes'];
  selectedDebtType: ReturnType<typeof useHub>['selectedDebtType'];
  allDebtTypes: ReturnType<typeof useHub>['debtTypes'];
  debtTypesLoaded: boolean;
  onOrgSwitch: (id: string) => void;
  onClientSelect: (client: (typeof clients)[0]) => void;
  onOperatorWideView: () => void;
  onCreateClient: () => void;
  onConfirmDebtTypeScope: (client: (typeof clients)[0], debtType: (typeof debtTypes)[0] | null) => void;
  onCreateDebtType: (client: (typeof clients)[0]) => void;
  showOperatorBook?: boolean;
  operatorBookLabel?: string;
  showMine?: boolean;
  mineSelected?: boolean;
  onMine?: () => void;
}) {
  const [activeClientId, setActiveClientId] = React.useState<string | null>(null);
  const operatorListRef = React.useRef<HTMLDivElement | null>(null);
  const clientListRef = React.useRef<HTMLDivElement | null>(null);
  const debtTypePanelRef = React.useRef<HTMLDivElement | null>(null);

  const focusFirstButton = (container: HTMLElement | null) => {
    container?.querySelector<HTMLButtonElement>('button:not(:disabled):not([data-arrow-skip])')?.focus();
  };

  const focusClientButton = React.useCallback((clientId: string) => {
    clientListRef.current?.querySelector<HTMLButtonElement>(`[data-client-id="${clientId}"]`)?.focus();
  }, []);

  const handleVerticalArrows = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return false;
    const buttons = Array.from(
      (e.currentTarget.closest('[data-arrow-list]') as HTMLElement | null)?.querySelectorAll<HTMLButtonElement>(
        'button:not(:disabled):not([data-arrow-skip])'
      ) ?? []
    );
    const idx = buttons.indexOf(e.currentTarget);
    if (idx === -1) return false;
    e.preventDefault();
    const next = e.key === 'ArrowDown' ? buttons[idx + 1] : buttons[idx - 1];
    next?.focus();
    return true;
  };

  // Prefer hover/focus pin; fall back to selected client so the panel stays after select.
  const panelClientId = activeClientId ?? selectedClient?.id ?? null;
  const activeClient = React.useMemo(() => clients.find((c) => c.id === panelClientId) ?? null, [clients, panelClientId]);

  const activeClientDebtTypes = React.useMemo(
    () => (activeClient ? allDebtTypes.filter((dt) => dt.client_id === activeClient.id) : []),
    [activeClient, allDebtTypes]
  );

  // Treat a client as having debt types until a successful fetch proves otherwise, so a
  // pending or failed request never collapses a client that actually has choices to offer.
  const hasDebtTypes = React.useCallback(
    (client: (typeof clients)[0]) => !debtTypesLoaded || allDebtTypes.some((dt) => dt.client_id === client.id),
    [allDebtTypes, debtTypesLoaded]
  );

  // A client with no debt types has nothing to choose between — every case is in the
  // same cohort — so its row commits the scope directly instead of opening the panel.
  const showDebtTypePanel = !!activeClient && hasDebtTypes(activeClient);

  return (
    <div className={cn('flex  min-h-[300px] max-h-[400px]', switching && 'opacity-50')}>
      {/* Operator Panel */}
      <div className="w-[250px] shrink-0 flex flex-col overflow-hidden border-r">
        <div className="px-3 h-10 shrink-0 flex items-center justify-between border-b border-border-light">
          <p className="text-muted-foreground text-xs font-medium">{orgColumnLabel}</p>
        </div>
        <div className="flex-1 overflow-hidden" ref={operatorListRef}>
          <ScrollArea className="h-full [&>[data-radix-scroll-area-viewport]>div]:block!">
            <div data-arrow-list className="space-y-1 px-1.5 py-2">
              {orgs.map((org) => (
                <button
                  type="button"
                  key={org.id}
                  disabled={switching}
                  onClick={() => onOrgSwitch(org.id)}
                  onKeyDown={(e) => {
                    if (handleVerticalArrows(e)) return;
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      focusFirstButton(clientListRef.current);
                    }
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                    'hover:bg-soft hover:dark:bg-card',
                    selectedOrgId === org.id &&
                      (isOperatorWideView
                        ? 'bg-[#FFE1D6]/25 border-[#FFE1D6] hover:bg-[#FFE1D6]/40 dark:bg-[#CC332B]/15 dark:border-[#FF7557]/25 hover:dark:bg-[#CC332B]/25'
                        : 'bg-accent/40 border-ring/25 hover:bg-accent/70 dark:bg-accent/75 dark:border-ring/40 dark:hover:bg-accent/95')
                  )}
                >
                  <Avatar className="size-7.5 rounded-sm shrink-0">
                    {org.avatarUrl && <AvatarImage src={org.avatarUrl} alt={org.name} />}
                    <AvatarFallback
                      className={cn(
                        'text-[10px] font-semibold rounded-md',

                        selectedOrgId === org.id
                          ? isOperatorWideView
                            ? 'bg-[#FFE1D6]/75 text-[#CC332B] dark:bg-[#CC332B]/15 dark:text-[#FF7557] dark:border-[#FF7557]/25 '
                            : 'bg-accent text-accent-foreground '
                          : 'bg-muted'
                      )}
                    >
                      {getInitials(org.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-sm">{org.name}</p>
                    {org.subtitle && <p className="text-xs text-muted-foreground">{org.subtitle}</p>}
                  </div>
                  {selectedOrgId === org.id && <ChevronRight className="size-3.5 text-muted-foreground shrink-0" />}
                </button>
              ))}
            </div>
          </ScrollArea>
          {orgs.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No {orgColumnLabel.toLowerCase()}s found</p>}
        </div>
      </div>

      {/* Client Panel */}
      <div
        data-arrow-list
        className="w-[300px] shrink-0 flex flex-col overflow-hidden"
        onMouseLeave={() => setActiveClientId(null)}
        ref={clientListRef}
      >
        <div className="px-3 h-10 shrink-0 flex items-center justify-between border-b border-border-light">
          <p className="text-muted-foreground text-xs font-medium">Clients</p>
          {canCreateClient && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-arrow-skip variant="outline" size="icon-xs" className="size-6" onClick={onCreateClient}>
                  <Plus className="size-3.5 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" align="center">
                Create New Client
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className="border-b border-border-light py-1.5 px-1.5">
          {showMine && (
            <button
              type="button"
              disabled={switching}
              onClick={onMine}
              className={cn(
                'mb-1 w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                'hover:bg-soft hover:dark:bg-card',
                mineSelected &&
                  'bg-[#FFE1D6]/25 border-[#FFE1D6] hover:bg-[#FFE1D6]/40 dark:bg-[#CC332B]/15 dark:border-[#FF7557]/25 hover:dark:bg-[#CC332B]/25'
              )}
            >
              <Layers className={cn('size-3.5 shrink-0', mineSelected ? 'text-[#CC332B] dark:text-[#FF7557]' : 'text-muted-foreground')} />
              <span className="truncate font-medium">My clients</span>
              {mineSelected && (
                <div className="ml-auto shrink-0 size-5 rounded-full flex items-center justify-center bg-[#FFE1D6]/75 text-[#CC332B] dark:bg-[#CC332B]/25 dark:text-[#FF7557]">
                  <Check className="size-3" />
                </div>
              )}
            </button>
          )}
          {showOperatorBook && (
            <button
              type="button"
              disabled={switching}
              onClick={onOperatorWideView}
              onMouseEnter={() => setActiveClientId(null)}
              onFocus={() => setActiveClientId(null)}
              onKeyDown={(e) => {
                if (handleVerticalArrows(e)) return;
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  focusFirstButton(operatorListRef.current);
                }
              }}
              className={cn(
                'w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                'hover:bg-soft hover:dark:bg-card',
                isOperatorWideView &&
                  'bg-[#FFE1D6]/25 border-[#FFE1D6] hover:bg-[#FFE1D6]/40 dark:bg-[#CC332B]/15 dark:border-[#FF7557]/25 hover:dark:bg-[#CC332B]/25'
              )}
            >
              <Layers
                className={cn('size-3.5 shrink-0', isOperatorWideView ? 'text-[#CC332B] dark:text-[#FF7557]' : 'text-muted-foreground')}
              />
              <span className="truncate font-medium">{operatorBookLabel}</span>
              {isOperatorWideView && (
                <div className="ml-auto shrink-0 size-5 rounded-full flex items-center justify-center bg-[#FFE1D6]/75 text-[#CC332B] dark:bg-[#CC332B]/25 dark:text-[#FF7557] dark:border-[#FF7557]/25">
                  <Check className="size-3" />
                </div>
              )}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full [&>[data-radix-scroll-area-viewport]>div]:block!">
            <div className="space-y-1 px-1.5 py-2">
              {clients.length > 0 &&
                clients.map((client) => {
                  const isSelected = selectedClient?.id === client.id;
                  const isHovered = activeClientId === client.id;

                  return (
                    <button
                      type="button"
                      key={client.id}
                      data-client-id={client.id}
                      disabled={switching}
                      onClick={() => {
                        setActiveClientId(client.id);
                        if (hasDebtTypes(client)) onClientSelect(client);
                        else onConfirmDebtTypeScope(client, null);
                      }}
                      onMouseEnter={() => setActiveClientId(client.id)}
                      onFocus={() => setActiveClientId(client.id)}
                      onKeyDown={(e) => {
                        if (handleVerticalArrows(e)) return;
                        if (e.key === 'ArrowLeft') {
                          e.preventDefault();
                          focusFirstButton(operatorListRef.current);
                          return;
                        }
                        if (e.key === 'ArrowRight' && hasDebtTypes(client)) {
                          e.preventDefault();
                          setActiveClientId(client.id);
                          requestAnimationFrame(() => focusFirstButton(debtTypePanelRef.current));
                        }
                      }}
                      className={cn(
                        'w-full min-w-0 flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                        'hover:bg-soft dark:hover:bg-card',
                        isSelected &&
                          'bg-accent/40 border-ring/25 hover:bg-accent/70 dark:bg-accent/75 dark:border-ring/40 dark:hover:bg-accent/95',
                        !isSelected && isHovered && 'bg-soft hover:dark:bg-card'
                      )}
                    >
                      <Avatar className="size-7.5 rounded-sm shrink-0">
                        {client.logo_url && <AvatarImage src={client.logo_url} alt={client.display_name} />}
                        <AvatarFallback
                          className={cn(
                            'text-[10px] font-semibold rounded-md',
                            isSelected ? 'bg-accent text-accent-foreground' : 'bg-muted'
                          )}
                        >
                          {getInitials(client.display_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium text-sm">{client.display_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{client.legal_name}</p>
                      </div>
                      {hasDebtTypes(client) ? (
                        <ChevronRight className="size-3.5 text-muted-foreground shrink-0" />
                      ) : (
                        isSelected && (
                          <div className="ml-auto shrink-0 size-5 rounded-full bg-accent flex items-center justify-center">
                            <Check className="size-3 text-accent-foreground" />
                          </div>
                        )
                      )}
                    </button>
                  );
                })}
            </div>
          </ScrollArea>

          {clients.length === 0 && !isOperatorWideView && (
            <p className="text-xs text-muted-foreground text-center py-4">No clients found</p>
          )}
        </div>
      </div>

      {/* Debt Type Panel - Conditional */}
      <div
        data-arrow-list
        ref={debtTypePanelRef}
        className={cn(
          'flex flex-col overflow-hidden transition-all duration-200 ease-in-out ',
          showDebtTypePanel
            ? 'w-[300px] opacity-100 translate-x-0 border-l border-border'
            : 'w-0 opacity-0 -translate-x-4 pointer-events-none'
        )}
        onMouseEnter={() => activeClient && setActiveClientId(activeClient.id)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' && activeClient) {
            e.preventDefault();
            focusClientButton(activeClient.id);
          }
        }}
      >
        {showDebtTypePanel && (
          <>
            <div className="px-3 h-10 shrink-0 flex items-center justify-between border-b border-border-light ">
              <p className="text-muted-foreground text-xs font-medium">Debt Types</p>
              {canCreateDebtType && activeClient && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-arrow-skip
                      variant="outline"
                      size="icon-xs"
                      className="size-6"
                      aria-label="Create debt type"
                      onClick={() => onCreateDebtType(activeClient)}
                    >
                      <Plus className="size-3.5 text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center">
                    Create Debt Type
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <div className="border-b border-border-light py-1.5 px-1.5 ">
              <button
                type="button"
                disabled={switching}
                onClick={() => {
                  if (activeClient) {
                    onConfirmDebtTypeScope(activeClient, null);
                  }
                }}
                onKeyDown={handleVerticalArrows}
                className={cn(
                  'w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                  'hover:bg-soft hover:dark:bg-card',
                  selectedClient?.id === activeClient?.id &&
                    !selectedDebtType &&
                    'bg-accent/40 border-ring/25 hover:bg-accent/70 dark:bg-accent/75 dark:border-ring/40 dark:hover:bg-accent/95'
                )}
              >
                <Folders
                  className={cn(
                    'size-3.5 shrink-0',
                    selectedClient?.id === activeClient?.id && !selectedDebtType ? 'text-accent-foreground' : 'text-muted-foreground'
                  )}
                />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <p className="truncate font-medium text-sm">{activeClient?.display_name ?? 'Client'}</p>
                  <Badge variant={selectedClient?.id === activeClient?.id && !selectedDebtType ? 'secondary' : 'subtle'} className="h-5">
                    Default
                  </Badge>
                </div>
                {selectedClient?.id === activeClient?.id && !selectedDebtType && (
                  <div className="ml-auto shrink-0 size-5 rounded-full bg-accent flex items-center justify-center">
                    <Check className="size-3 text-accent-foreground" />
                  </div>
                )}
              </button>
            </div>

            <div className="flex-1 overflow-hidden min-w-[250px] ">
              <ScrollArea className="h-full [&>[data-radix-scroll-area-viewport]>div]:block!">
                <div className="space-y-1 px-1.5 py-2">
                  {activeClientDebtTypes.map((debtType) => (
                    <button
                      type="button"
                      key={debtType.id}
                      disabled={switching}
                      onClick={() => {
                        if (activeClient) {
                          onConfirmDebtTypeScope(activeClient, debtType);
                        }
                      }}
                      onKeyDown={handleVerticalArrows}
                      className={cn(
                        'w-full min-w-0 flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                        'hover:bg-soft dark:hover:bg-card',
                        selectedClient?.id === activeClient?.id &&
                          selectedDebtType?.id === debtType.id &&
                          'bg-accent/40 border-ring/25 hover:bg-accent/70 dark:bg-accent/75 dark:border-ring/40 dark:hover:bg-accent/95'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium text-sm">{debtType.name}</p>
                        {debtType.description && <p className="text-xs text-muted-foreground truncate">{debtType.description}</p>}
                      </div>
                      {selectedClient?.id === activeClient?.id && selectedDebtType?.id === debtType.id && (
                        <div className="ml-auto shrink-0 size-5 rounded-full bg-accent flex items-center justify-center">
                          <Check className="size-3 text-accent-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {activeClient && (
                  <div className="border-t border-border-light py-1.5 px-1.5 flex-1">
                    <button
                      type="button"
                      disabled={switching}
                      onClick={() => {
                        onConfirmDebtTypeScope(activeClient, makeNoDebtTypeCohort(activeClient));
                      }}
                      onKeyDown={handleVerticalArrows}
                      className={cn(
                        'w-full min-w-0 flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors border border-transparent',
                        'hover:bg-soft dark:hover:bg-card',
                        selectedClient?.id === activeClient?.id &&
                          isNoDebtTypeCohort(selectedDebtType) &&
                          'bg-accent/40 border-ring/25 hover:bg-accent/70 dark:bg-accent/75 dark:border-ring/40 dark:hover:bg-accent/95'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium text-sm">Others</p>
                        <p className="text-xs text-muted-foreground truncate">Cases without debt type</p>
                      </div>
                      {selectedClient?.id === activeClient?.id && isNoDebtTypeCohort(selectedDebtType) && (
                        <div className="ml-auto shrink-0 size-5 rounded-full bg-accent flex items-center justify-center">
                          <Check className="size-3 text-accent-foreground" />
                        </div>
                      )}
                    </button>
                  </div>
                )}
              </ScrollArea>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
